/**
 * On-device perf runner (P0-T13, SPEC-13 §2.1).
 * Open the game with `?perf=<path>&tier=<t>` (optionally `&upload=1` for the
 * page's own origin, or `&upload=<serverUrl>` for another server): the intro is
 * skipped, the scripted camera path from `public/perf/<path>.json` is replayed,
 * and per-frame samples are collected. When done, `__KN.perf.json()` returns
 * the SPEC-01 §8 JSON; with `&upload=` it POSTs it to /perf-results.
 */
import type { Scene } from 'babylonjs';
import type { CameraHandle } from '../avatar/camera';
import { readGpuString } from '../engine/tiers';
import type { KNGlobal } from './kn';

interface Keyframe {
  t: number; // ms into the run
  x: number;
  y: number;
  z: number;
  alpha?: number;
  beta?: number;
  radius?: number;
}
export interface PerfPath {
  durationMs: number;
  keyframes: Keyframe[];
  /** gate P1-G03: paths may pin world time (hours) and weather */
  timeHours?: number;
  weather?: string;
}

export interface PerfResult {
  scene: string;
  tier: string;
  device: string;
  gpu: string;
  frames: number;
  frameMs: { p50: number; p95: number; max: number };
  jsMs: { p95: number };
  drawCalls: { p95: number };
  triangles: { p95: number };
  textureMB: number;
  heapMB: { start: number; end: number };
  shed: string[];
}

function quantile(sorted: number[], q: number): number {
  if (!sorted.length) return 0;
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * q))] ?? 0;
}

function deviceLabel(): string {
  const ua = navigator.userAgent;
  // UA data is unreliable (iOS CriOS, etc.) — the server records the raw UA
  // alongside; this label only needs to disambiguate devices at a glance.
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  const android = /Android[^)]*\(([^)]*)\)/.exec(ua) ?? /; ([^;)]+) Build/.exec(ua);
  if (android?.[1]) return `Android ${android[1].trim().slice(0, 48)}`;
  if (/Android/i.test(ua)) return 'Android device';
  return (
    'desktop ' +
    (/headless/i.test(ua)
      ? 'headless'
      : /Firefox/i.test(ua)
        ? 'firefox'
        : /Safari/i.test(ua) && !/Chrome/i.test(ua)
          ? 'safari'
          : 'chromium')
  );
}

function heapMB(): number | null {
  const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
  return mem ? Math.round(mem.usedJSHeapSize / (1024 * 1024)) : null;
}

export function maybeStartPerfRunner(
  scene: Scene,
  camera: CameraHandle,
  kn: KNGlobal,
  shedLog: string[],
  skipIntro: () => void,
): void {
  const params = new URLSearchParams(location.search);
  const pathName = params.get('perf');
  if (!pathName || !/^[\w-]+$/.test(pathName)) return;

  void (async () => {
    skipIntro();
    let path: PerfPath;
    try {
      const res = await fetch(`/perf/${encodeURIComponent(String(pathName))}.json`);
      path = (await res.json()) as PerfPath;
    } catch {
      console.error(`perf: path '${pathName}' not found`);
      return;
    }

    // scripted environment (gate P1-G03 runs slice-walk at dusk in rain)
    if (typeof path.timeHours === 'number') {
      (window as unknown as { __KN?: { setWorldTimeHours?: (h: number) => void } }).__KN?.setWorldTimeHours?.(
        path.timeHours,
      );
    }
    if (path.weather) {
      (window as unknown as { __KN?: { setWeather?: (k: string | null) => void } }).__KN?.setWeather?.(
        path.weather,
      );
    }

    const heapStart = heapMB() ?? 0;
    let gpu = 'unknown';
    void readGpuString().then((g) => {
      gpu = g;
    });
    const frameMs: number[] = [];
    const jsMs: number[] = [];
    const draws: number[] = [];
    const tris: number[] = [];
    let last = performance.now();
    const t0 = last;
    let lastDelta = 0;
    // SPEC-13 §2.1: 2-minute warm-up before the scripted path, so shader
    // compiles and first-load hitches never enter the sampled window.
    // `?warmup=<ms>` overrides (headless counts-only runs use 0).
    const warmupParam = Number(params.get('warmup') ?? 120000);
    const warmupMs = Number.isFinite(warmupParam) && warmupParam >= 0 ? warmupParam : 120000;

    scene.onAfterRenderObservable.add(() => {
      lastDelta = scene.getEngine().getDeltaTime();
    });

    camera.setMode('city');
    const kf = path.keyframes;

    kn.perf = { done: false, json: () => null };

    scene.onBeforeRenderObservable.add(() => {
      const now = performance.now();
      const t = now - t0 - warmupMs;
      if (t < 0) {
        last = now; // warm-up: keep running the game, sample nothing
        return;
      }
      if (t > path.durationMs) return;
      // interpolate the path
      let i = 0;
      while (i + 1 < kf.length && (kf[i + 1]?.t ?? 0) <= t) i++;
      const a = kf[i];
      const b = kf[i + 1] ?? kf[i];
      if (!a || !b) return;
      const span = Math.max(1, b.t - a.t);
      const u = Math.min(1, Math.max(0, (t - a.t) / span));
      const s = u * u * (3 - 2 * u); // smoothstep
      camera.follow({ x: a.x + (b.x - a.x) * s, y: a.y + (b.y - a.y) * s, z: a.z + (b.z - a.z) * s }, null);
      if (a.alpha !== undefined && b.alpha !== undefined)
        camera.cam.alpha = a.alpha + (b.alpha - a.alpha) * s;
      if (a.beta !== undefined && b.beta !== undefined) camera.cam.beta = a.beta + (b.beta - a.beta) * s;
      if (a.radius !== undefined && b.radius !== undefined)
        camera.cam.radius = a.radius + (b.radius - a.radius) * s;

      const dt = now - last;
      last = now;
      if (frameMs.length > 0 || t > 500) {
        // skip the first 500 ms of warm-up
        frameMs.push(dt);
        jsMs.push(lastDelta);
        draws.push(scene.getActiveMeshes().length);
        tris.push(triCount(scene));
      }
    });

    const finish = (): void => {
      const sorted = (arr: number[]): number[] => [...arr].sort((x, y) => x - y);
      const sf = sorted(frameMs);
      const stats = kn.stats();
      const result: PerfResult = {
        scene: String(pathName),
        tier: kn.tier,
        device: deviceLabel(),
        gpu,
        frames: sf.length,
        frameMs: { p50: quantile(sf, 0.5), p95: quantile(sf, 0.95), max: sf[sf.length - 1] ?? 0 },
        jsMs: { p95: quantile(sorted(jsMs), 0.95) },
        drawCalls: { p95: quantile(sorted(draws), 0.95) },
        triangles: { p95: quantile(sorted(tris), 0.95) },
        textureMB: textureMemoryMB(scene),
        heapMB: { start: heapStart, end: heapMB() ?? heapStart },
        shed: [...shedLog, ...stats.shedSteps],
      };
      if (kn.perf) {
        kn.perf.done = true;
        kn.perf.json = () => result;
      }
      // stash for headless capture (tools/perf/capture.ts) — survives even if the
      // renderer then dies under SwiftShader
      (window as unknown as { __KN_PERF_JSON?: string }).__KN_PERF_JSON = JSON.stringify(result);
      const upload = params.get('upload');
      if (upload) {
        // `upload=1` (or `true`) means "this page's origin"; anything else is
        // treated as a full server URL (e.g. a Vercel preview posting to VPS).
        // Bare `1` must NOT be concatenated: `fetch('1/perf-results')` resolves
        // against the page path and 405s on the Colyseus matcher.
        const base = upload === '1' || upload === 'true' ? '' : upload.replace(/\/$/, '');
        void fetch(`${base}/perf-results`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(result),
        }).catch(() => undefined);
      }
    };
    window.setTimeout(finish, warmupMs + path.durationMs + 250);

    // SPEC-13 §2.1: the page shows its state so nobody re-taps or reloads
    // mid-run (a reload restarts the 2-minute warm-up).
    const banner = document.createElement('div');
    banner.setAttribute(
      'style',
      'position:fixed;top:0;left:0;right:0;z-index:9999;padding:10px 12px;' +
        'background:rgba(0,0,0,.75);color:#fff;font:14px/1.4 system-ui;text-align:center;',
    );
    document.body.appendChild(banner);
    const tick = (): void => {
      const el = (performance.now() - t0) / 1000;
      banner.textContent =
        (kn.perf?.done ?? false)
          ? 'Done · result sent — you may close this page'
          : el < warmupMs / 1000
            ? `Measuring… warming up ${Math.ceil(warmupMs / 1000 - el)} s — do not touch`
            : el < (warmupMs + path.durationMs) / 1000
              ? `Measuring… running path ${Math.ceil((warmupMs + path.durationMs) / 1000 - el)} s — do not touch`
              : 'Measuring… sending result';
    };
    tick();
    window.setInterval(tick, 500);
  })();
}

function triCount(scene: Scene): number {
  const direct = scene.getTotalVertices();
  if (direct > 0) return Math.round(direct / 3);
  let verts = 0;
  const active = scene.getActiveMeshes();
  for (let i = 0; i < active.length; i++) verts += active.data[i]?.getTotalVertices() ?? 0;
  return Math.round(verts / 3);
}

function textureMemoryMB(scene: Scene): number {
  let bytes = 0;
  for (const tex of scene.textures) {
    const size = tex.getSize();
    bytes += size.width * size.height * 4;
  }
  return Math.round(bytes / (1024 * 1024));
}
